/**
 * `GlobalConversationLayout` — dedicated route for Lexiun's
 * single, persistent global chat thread.
 *
 * Sibling of `HomeLayout`: HomeLayout renders the `/home` welcome
 * surface (greeting + status card + recent-case tiles), this layout
 * renders the docked `ConversationShell` (scrolling transcript +
 * fixed-bottom composer) for the global session.
 *
 * Route entry points:
 *   - `/chat`               → global conversation
 *   - Sending a message from the welcome composer → auto-navigate
 *     to `/chat` so the reply renders in the proper transcript
 *     layout (not awkwardly inside the welcome composer card).
 *   - The "Open →" button on the global tile in `/home`'s
 *     recent-conversations list.
 *
 * Lexiun has exactly ONE global thread — there are no "new" chats,
 * ever. This route is a singleton; no `:conversationId` segment.
 */

import { useTranslation } from "react-i18next";
import { useChatSession } from "@/hooks/useChatSession";
import { useBrowserActivityFromAgent } from "@/hooks/useBrowserActivityFromAgent";
import { useFileOpenFromAgent } from "@/hooks/useFileOpenFromAgent";
import { useDerivedChatSessionKey } from "@/hooks/useDerivedChatSessionKey";
import { useResourceCopyMoveFromAgent } from "@/hooks/useResourceCopyMoveFromAgent";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { buildLxSessionKey } from "@/types/session";
import { ChatInput } from "@/components/workspace/ChatInput";
import { ChatLLMKeysGate } from "@/components/chat/ChatLLMKeysGate";
import { ChatInfobar } from "@/components/chat/ChatInfobar";
import { ConversationShell } from "@/components/chat/ConversationShell";
import { FeedbackProvider } from "@/components/feedback/FeedbackProvider";

const GLOBAL_SESSION_KEY = buildLxSessionKey({ type: "global" });

export function GlobalConversationLayout() {
  const { t } = useTranslation();
  const connectionState = useGatewayStore((s) => s.connectionState);
  const connected = connectionState === "connected";

  const {
    messages,
    streamingBlocks,
    isStreaming,
    activity,
    error,
    send,
    abort,
    retryLast,
    isStalled,
    releaseStalledRun,
    historyReadError,
    hasMoreHistory,
    loadingOlder,
    olderError,
    loadOlder,
  } = useChatSession(GLOBAL_SESSION_KEY);

  // CCM Wave 2 (org-pane handoff): the SidePanel is no longer
  // force-collapsed here. On the global chat thread it now renders the
  // per-user org ("Global") workspace file tree (`CaseNavigator
  // noCaseView="org"` in `SidePanel`), so the lawyer can browse + open
  // their org-scope files exactly like a case's. The user's manual
  // collapse/expand preference (persisted in `useUIStore`) stands.

  // P4.D.CDP / D1: surface the Lexiun Browser pane discoverability
  // toast when the agent calls lx_browser_navigate from a global chat
  // turn. Same hook as CaseWorkspaceLayout uses; activeMode guard
  // suppresses the toast if the user is already on /agent-browser.
  useBrowserActivityFromAgent();

  // CCM Wave 2 — honour the concierge's `lx_open_file` from the global
  // thread, so an org-scope file the agent surfaces opens in the
  // shell-level `WorkspaceFilePaneShell` (route-agnostic) exactly like a
  // case file. `useFileBytes` org-branches the byte read by effective
  // scope, so the file resolves against the per-user "Global" workspace.
  // Gated to the global conversation's own session key — a case thread's
  // open must not fan into this window (instance 2, cross-case reads).
  useFileOpenFromAgent(useDerivedChatSessionKey());

  // CCM §1.7 — keep the org workspace tree consistent when the concierge runs
  // an `lx_copy_resource` / `lx_move_resource` tool (the NL "move it to …"
  // path), invalidating the same query keys the SPA mutation uses.
  useResourceCopyMoveFromAgent();

  const composer = (
    <ChatLLMKeysGate>
      <ChatInput
        showModel
        enableAttach
        onSend={send}
        onAbort={abort}
        isStreaming={isStreaming}
        disabled={!connected}
        historyScope="global"
      />
    </ChatLLMKeysGate>
  );

  return (
    <FeedbackProvider context={{ sessionKey: GLOBAL_SESSION_KEY }}>
      <ConversationShell
        stalled={isStalled}
        onReleaseStalledRun={releaseStalledRun}
        testId="global-conversation-shell"
        role="main"
        ariaLabel={t("home.tile.globalTitle")}
        dataSessionKey={GLOBAL_SESSION_KEY}
        messages={messages}
        streamingBlocks={streamingBlocks}
        isStreaming={isStreaming}
        activity={activity}
        error={error}
        onRetry={retryLast}
        historyReadError={historyReadError}
        hasMoreHistory={hasMoreHistory}
        loadingOlder={loadingOlder}
        olderError={olderError}
        onLoadOlder={loadOlder}
        composer={composer}
        footer={<ChatInfobar />}
      />
    </FeedbackProvider>
  );
}
